import React, { useState, useEffect, useRef } from 'react';
import { Modal as ModalComponent, closeModal } from '../../common/Modal.jsx';
import { Portal } from '../../common/Portal.jsx';
import { FaIcon } from '../../common/Icons.jsx';
import Stepper from '../../common/Stepper.jsx';
import { Button, Row, Col } from '../../bootstrap/index.jsx';
import { fetchJson } from '../../../helpers/index.js';
import {createAttachment} from '../../../helpers/create_attachment.js';
import { getDisplayedRowsToImport } from './settings.js';
import styles from './ImportWizard.module.scss';

import {
  datagrid_import_rows_path
} from '~/routes';

import Grid from './Grid.jsx'

const MODAL_ID = 'import-wizard-modal'
export const DEFAULT_SETTINGS_STATE = {
  ws_name: '',
  ws_header_idx: -1,
  all_rows: true,
  range_from: '',
  range_to: '',
  columns: {}
}

export function LabelWithDataType({ step }){
  return (
    <div className='d-flex'>
      {step.label}
      {
        step.dataType &&
        <div style={{ opacity: 0.7, marginLeft: '0.5rem'}}>
          ({step.dataType})
        </div>
      }
    </div>
  )
}

import {validationDisplayCount} from './helpers.js';

import useSteps from './useSteps.jsx';
import useGridState from './useGridState.js';

export default function ImportWizardModal({handleCloseModal, context, datagrid}) {
  const [exportReady, setExportReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const gridRef = useRef();
  const [activeStep, setActiveStep] = useState(0);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS_STATE);
  const [fileSizeError, setFileSizeError] = useState(false);  

  const {toggleGrid, gridState} = useGridState('excel', gridRef);

  const updateSettings = (newSettings) => {
    setSettings(prev => ({...prev, ...newSettings}));
  }

  const markReadyForExport = () => { setExportReady(true) };

  const {steps, xlsWorkBook: {file}} = useSteps(
    gridRef,
    datagrid, 
    settings,
    updateSettings,
    undefined,
    fileSizeError,
    setFileSizeError,
    markReadyForExport,
    context,
    toggleGrid
  );

  const onStepChange = (step) => {
    if (fileSizeError){
      return;
    }
    if (steps.length == step){ 
      if (!steps.every(step => {
        if(!(step.completed !== false)) console.log(step);
        return step.completed !== false 
      } )){
        alert("Configure all steps"); //WIP
        return;
      }
      markReadyForExport();
    }
    setActiveStep(step);
  }

  // Display a warning when exiting the dialog that the configuration will be lost
  useEffect(() => {
    jQuery(`#${MODAL_ID}`).on('hide.bs.modal', (e) => {
      e.stopImmediatePropagation();
      if (!confirm(I18n.t('close_modal_warning'))) e.preventDefault();
    })
  }, []);

  const handleClose = () => {
    jQuery(`#${MODAL_ID}`).off('hide.bs.modal');
    closeModal(MODAL_ID);
    handleCloseModal();
  };

  const handleSave = async (e) => {
    // Uploads the file and get the attachment details
   const importPermissions = {'datagrid.show': {id: context.project_id, datagrid_id: context.id, type: 'project'}};
   const attachment = await createAttachment(file, importPermissions);

    e.preventDefault();
    setSaving(true);
    fetchJson(
      datagrid_import_rows_path({id: context.id, project_id: context.project_id, context_name: context.context_name, _options: true}),
        {
          method: 'POST',
          body: JSON.stringify({
            transactions: getDisplayedRowsToImport(gridRef, datagrid).map((c, i) => {
              return {add: [c]}
            }),
            attachment_id: attachment.attachment_id,
            attachment_path: attachment.path,
            first_data_row: settings.all_rows ? 1 :  settings.range_from || 1, // The starting row number to import data from the Excel file
            xls_worksheet: settings.ws_name
          })
        }
    ).then(res => {
      setSaving(false);
      handleClose();
      //Turbo.visit(location.href);
      window.ToastNotification({message: I18n.t('datagrid_import_started_info'), horizontal: 'center', vertical: 'top', autoHideDuration: 5});
    })
  };


  return (
    <Portal
      id={MODAL_ID}
      size='full-width'
      title={I18n.t('excel_import_wizard')}
      saveLabel={I18n.t('import')}
      cancelLabel={I18n.t('close')}
      onClose={handleClose}
      onSave={handleSave}
      saving={saving}
      disableSave={!exportReady}
      component={ModalComponent}
    >
      <Row>
        <Col size='xs-4' className='thin-scrollbar' style={{height: 'calc(-100px + 90vh)', overflowY: 'scroll'}}>
          <Stepper 
            steps={steps}
            activeStep={activeStep}
            onStepChange={onStepChange}
            LabelComponent={LabelWithDataType}
          />            
        </Col>
        <Col size='xs-8'>
          <div className="btn-group mb-1">
            <Button size='sm' type={gridState == 'excel' ? 'primary' : 'default'} onClick={() => toggleGrid('excel')}>
              <FaIcon type='file-excel-o'/> {I18n.t('excel')}
            </Button>
            <Button size='sm' type={gridState == 'datagrid' ? 'primary' : 'default'} onClick={() => toggleGrid('datagrid')}>
              <FaIcon type='table' /> {I18n.t('grid')}
            </Button>
          </div>
          <ColumnErrorNavigation
            validationErrors={steps[activeStep]?.validationErrors}
            gridRef={gridRef}
          />
          <Grid {...{gridRef, settings}} className={styles['grid']}/>
        </Col>
      </Row>
    </Portal>
  );
};

export function ColumnErrorNavigation({validationErrors, gridRef}) {
  const [chosenError, setChosenError] = useState(-1);

  // Reset to start on new validation Errors
  useEffect(() => {
    setChosenError(0)
  }, [validationErrors])

  const handleBack = () => {
    const prev = (chosenError - 1 + validationErrors.length) % validationErrors.length;
    jumpToRow(Number(prev))
    setChosenError(prev) 
  }

  const handleNext = () => {
    const next = (chosenError + 1) % validationErrors.length;
    jumpToRow(Number(next))
    setChosenError(next)
  }

  const jumpToRow = (rowIndex) => {
    gridRef.current.api.ensureIndexVisible(rowIndex)
    const rowNode = gridRef.current.api.getDisplayedRowAtIndex(rowIndex)

    // Some delay after scrolling
    setTimeout(() => {
      gridRef.current.api.flashCells({
        rowNodes: [rowNode]})
    }, 300)
  }

  if (!validationErrors?.length) return null;

  const count = validationDisplayCount(validationErrors);

  return <div className="btn-group mb-1 ml-1 text-danger">
    <FaIcon type='warning'/>
    <b className='mr-1 ml-1'>
      {I18n.t('import_validation_errors_long', {count: count})}
    </b>
    <span size='sm' type='default' onClick={handleBack} className={styles['error-pagination']}>
      {I18n.t('prev').toUpperCase()}
    </span>
    |
    <span size='sm' type='default' onClick={handleNext} className={styles['error-pagination']}>
      {I18n.t('next').toUpperCase()}
    </span>
  </div>
}